import { StateCreator, create } from 'zustand';
import {
  OrganizationsState,
  createOrganizationsStore,
} from './slices/OrganizationsSlice';
import { ConversationHistoryInterface } from '../utils/types';
import { axiosInstance } from '../utils/axiosApp';

import i18n from '../../i18n';
import {
  getAccountSession,
  subscribeAccountSession,
} from '../auth/accountSession';
import { listMyGroups, StoredGroup } from '../groups/GroupsApi';
import { filterDeletedGroupSources } from '../settings/planStudio/profilePlanLibraryUtils';
import {
  AccountPreferencesState,
  createAccountPreferencesSlice,
} from './slices/AccountPreferencesSlice';
import {
  createProfilePreferencesStateStore,
  ProfilePreferencesState,
} from './slices/ProfilePreferencesSlice';
import {
  createProfilePlansStateStore,
  ProfilePlansState,
} from './slices/ProfilePlansSlice';
import {
  ProfileAccessState,
  createProfileAccessStore,
} from './slices/profileAccessSlice';
import {
  createProfileSessionStore,
  durationTranslations,
  ProfileSessionState,
  sessionTranslations,
} from './slices/ProfileSessionSlice';
import { createUiSlice, UiState } from './slices/ShowAddProfileSlice';
import {
  createGroupNavigationSlice,
  GroupNavigationState,
} from './slices/GroupNavigationSlice';
import {
  createContextSwitchSlice,
  ContextSwitchState,
} from './slices/ContextSwitchSlice';

const hasLoadedTranslationBundle = (): boolean => {
  const candidates = [
    i18n.resolvedLanguage,
    i18n.language,
    ...(i18n.languages ?? []),
  ].filter((value): value is string => Boolean(value));

  return candidates.some((lang) => i18n.hasResourceBundle(lang, 'translation'));
};

function updateSessionTranslations() {
  if (!hasLoadedTranslationBundle()) {
    return;
  }
  sessionTranslations.inProgress = i18n.t('sessionsList.inProgress');
  sessionTranslations.finalizationPending = i18n.t(
    'sessionsList.finalizationPending'
  );
  sessionTranslations.finalizationPendingDuration = i18n.t(
    'sessionsList.finalizationPendingDuration'
  );
}

export function updateDurationTranslations() {
  if (!hasLoadedTranslationBundle()) {
    return;
  }
  durationTranslations.oldOngoing = durationTranslations.ongoing;
  durationTranslations.oldHour = durationTranslations.hour;
  durationTranslations.oldMinute = durationTranslations.minute;
  durationTranslations.oldSecond = durationTranslations.second;

  durationTranslations.ongoing = i18n.t('duration.ongoing');
  durationTranslations.hour = i18n.t('duration.hour', { count: 1 });
  durationTranslations.minute = i18n.t('duration.minute', { count: 1 });
  durationTranslations.second = i18n.t('duration.second', { count: 1 });
}

export interface GroupsState {
  groups: StoredGroup[];
  setGroups: (groups: StoredGroup[]) => void;
  deletedGroupIds: ReadonlySet<string>;
  markGroupDeleted: (id: string) => void;

  groupsLoading: boolean;
  setGroupsLoading: (v: boolean) => void;

  activeGroupId: string | null;
  activeGroup: StoredGroup | null;
  setActiveGroupId: (id: string | null) => void;

  fetchGroups: () => Promise<void>;
}

export const createGroupsStore: StateCreator<
  GroupsState & Pick<ProfilePlansState, 'profilePlanLibrary'>,
  [],
  [],
  GroupsState
> = (set, get) => ({
  groups: [],
  setGroups: (groups) => {
    const { activeGroupId, deletedGroupIds } = get();
    groups = groups.filter((group) => !deletedGroupIds.has(group.group_id));
    const activeGroup = activeGroupId
      ? (groups.find((group) => group.group_id === activeGroupId) ?? null)
      : null;
    set({ groups, activeGroup });
  },
  // Keep confirmed deletions for this account session so older reads cannot
  // restore removed groups in either the shared list or organization views.
  deletedGroupIds: new Set(),
  markGroupDeleted: (id) =>
    set((state) => {
      const deletedGroupIds = new Set([...state.deletedGroupIds, id]);
      return {
        deletedGroupIds,
        profilePlanLibrary: state.profilePlanLibrary
          ? filterDeletedGroupSources(state.profilePlanLibrary, deletedGroupIds)
          : null,
        groups: state.groups.filter((group) => group.group_id !== id),
        activeGroupId: state.activeGroupId === id ? null : state.activeGroupId,
        activeGroup:
          state.activeGroup?.group_id === id ? null : state.activeGroup,
      };
    }),
  groupsLoading: false,
  setGroupsLoading: (v) => set({ groupsLoading: v }),

  activeGroupId: null,
  activeGroup: null,
  setActiveGroupId: (id) => {
    const { groups } = get();
    const activeGroup = id
      ? (groups.find((group) => group.group_id === id) ?? null)
      : null;
    set({ activeGroupId: id, activeGroup });
  },

  fetchGroups: async () => {
    set({ groupsLoading: true });
    try {
      const groups = await listMyGroups();
      get().setGroups(groups);
    } catch (err) {
      console.error('Error while fetching groups:', err);
      set({ groups: [], activeGroup: null });
    } finally {
      set({ groupsLoading: false });
    }
  },
});

interface ConversationStore {
  conversations: { [key: number]: ConversationHistoryInterface[] };
  setConversations: (conversations: {
    [key: number]: ConversationHistoryInterface[];
  }) => void;
  addMessages: (
    messages: ConversationHistoryInterface[],
    sessionNumber: number
  ) => void;
  lastMessageId: { [key: number]: string };
  setLastMessageId: (sessionNumber: number, lastMessageId: string) => void;
  hasMoreMessages: boolean;
  setHasMoreMessages: (has_more_messages: boolean) => void;
  resetConversationStore: () => void;
}

const createConversationStore: StateCreator<ConversationStore> = (
  set,
  get
) => ({
  conversations: {},
  lastMessageId: {},
  hasMoreMessages: true,
  resetConversationStore: () =>
    set({
      conversations: {},
      lastMessageId: {},
      hasMoreMessages: true,
    }),
  setConversations: (conversations) => set({ conversations }),

  addMessages: (messages, sessionNumber) =>
    set((state) => {
      const existingMessages = state.conversations[sessionNumber] || [];

      const newMessages = messages.filter(
        (message) =>
          !existingMessages.some(
            (existingMessage) => existingMessage.id === message.id
          )
      );

      const updatedMessages = [...existingMessages, ...newMessages];

      // Sort messages by timestamp
      const sortedMessages = updatedMessages.sort(
        (a, b) =>
          new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      );

      const updatedConversations = {
        ...state.conversations,
        [sessionNumber]: sortedMessages,
      };

      const lastMessage =
        sortedMessages.length > 0
          ? sortedMessages[sortedMessages.length - 1]
          : null;
      if (lastMessage) {
        get().setLastMessageId(sessionNumber, lastMessage.id);
      }

      return {
        conversations: updatedConversations,
      };
    }),

  setLastMessageId: (sessionNumber, lastMessageId) =>
    set((state) => ({
      lastMessageId: {
        ...state.lastMessageId,
        [sessionNumber]: lastMessageId,
      },
    })),

  setHasMoreMessages: (has_more_messages) =>
    set({
      hasMoreMessages: has_more_messages,
    }),
});

interface SubscribersState {
  subscribers: string[];
  loading: boolean;
  error: string | null;
  fetchSubscribers: () => Promise<void>;
  addSubscriber: (email: string) => void;
  removeSubscriber: (email: string) => void;
}

const createSubscribersStateStore: StateCreator<SubscribersState> = (set) => ({
  subscribers: [],
  loading: true,
  error: null,
  fetchSubscribers: async () => {
    set({ loading: true, error: null });

    try {
      const response = await axiosInstance().get('/api/subscribers');
      if (response.status === 200) {
        set({
          subscribers: response.data?.emails ?? [],
          loading: false,
        });
      } else {
        throw new Error('Failed to fetch subscribers');
      }
    } catch (error) {
      console.error('Error fetching subscribers:', error);
      set({
        error: 'An error occurred while fetching settings',
        loading: false,
      });
    }
  },
  addSubscriber: (email) =>
    set((state) => {
      if (state.subscribers.includes(email)) {
        console.warn(`Subscriber ${email} already exists.`);
        return state;
      }
      return {
        subscribers: [...state.subscribers, email],
      };
    }),
  removeSubscriber: (email) =>
    set((state) => {
      if (!state.subscribers.includes(email)) {
        console.warn(`Subscriber ${email} not found.`);
        return state;
      }
      return {
        subscribers: state.subscribers.filter((sub) => sub !== email),
      };
    }),
});

interface PaymentState {
  balance: number | null;
  loadingBalance: boolean;
  fetchBalance: () => Promise<void>;
}

export const createPaymentStore: StateCreator<PaymentState> = (set) => ({
  balance: null,
  loadingBalance: true,

  fetchBalance: async () => {
    set({ loadingBalance: true });
    try {
      const response = await axiosInstance().get('/api/payments/balance', {
        withCredentials: true,
      });
      set({ balance: Number(response.data.balance) });
    } catch (error) {
      console.error('Failed to fetch balance:', error);
    } finally {
      set({ loadingBalance: false });
    }
  },
});

type BoundState = AccountPreferencesState &
  ConversationStore &
  ProfileSessionState &
  ProfilePreferencesState &
  SubscribersState &
  ProfilePlansState &
  PaymentState &
  ProfileAccessState &
  GroupsState &
  OrganizationsState &
  GroupNavigationState &
  ContextSwitchState &
  UiState;

const createAccountState: StateCreator<BoundState> = (set, get, api) => {
  const session = getAccountSession();
  // Every action belongs to the session that created it. Pending work from a
  // previous account cannot write into the replacement account's store.
  const setForSession: typeof set = (
    ...args:
      | [
          partial:
            | BoundState
            | Partial<BoundState>
            | ((state: BoundState) => BoundState | Partial<BoundState>),
          replace?: false,
        ]
      | [state: BoundState | ((state: BoundState) => BoundState), replace: true]
  ) => {
    if (getAccountSession() !== session) return;
    if (args[1] === true) set(args[0], true);
    else set(args[0], args[1]);
  };
  let initialState: BoundState;
  // Indirect calls such as get().setGroups() must also keep their old actions.
  const getForSession: typeof get = () =>
    getAccountSession() === session ? get() : initialState;
  initialState = {
    ...createAccountPreferencesSlice(setForSession, getForSession, api),
    ...createConversationStore(setForSession, getForSession, api),
    ...createProfileSessionStore(setForSession, getForSession, api),
    ...createProfilePreferencesStateStore(setForSession, getForSession, api),
    ...createSubscribersStateStore(setForSession, getForSession, api),
    ...createProfilePlansStateStore(setForSession, getForSession, api),
    ...createPaymentStore(setForSession, getForSession, api),
    ...createProfileAccessStore(setForSession, getForSession, api),
    ...createGroupsStore(setForSession, getForSession, api),
    ...createOrganizationsStore(setForSession, getForSession, api),
    ...createGroupNavigationSlice(setForSession, getForSession, api),
    ...createContextSwitchSlice(setForSession, getForSession, api),
    ...createUiSlice(setForSession, getForSession, api),
  };
  const setProfile = initialState.setActiveProfileId;
  const setGroup = initialState.setActiveGroupId;
  initialState.setActiveProfileId = (id) => {
    if (id) setForSession({ activeOrganizationId: null });
    setProfile(id);
  };
  initialState.setActiveGroupId = (id) => {
    if (id) setForSession({ activeOrganizationId: null });
    setGroup(id);
  };
  return initialState;
};

export const useBoundStore = create<BoundState>(createAccountState);

subscribeAccountSession(() => {
  useBoundStore.setState(
    createAccountState(
      useBoundStore.setState,
      useBoundStore.getState,
      useBoundStore
    ),
    true
  );
});

if (i18n.isInitialized) {
  updateSessionTranslations();
  updateDurationTranslations();
}

i18n.on('languageChanged', () => {
  updateSessionTranslations();
  updateDurationTranslations();
  useBoundStore.getState().refreshSessionTranslations?.();
  useBoundStore.getState().refreshDurationTranslations?.();
});
i18n.on('loaded', () => {
  updateSessionTranslations();
  updateDurationTranslations();
  useBoundStore.getState().refreshSessionTranslations?.();
  useBoundStore.getState().refreshDurationTranslations?.();
});
